import { useMemo, useCallback, useState, useEffect } from "react";
import { Button } from "@aidkitorg/component-library";
import { DateRangeFilter } from "./DateRangeFilter";
import { EventTypeFilter } from "./EventTypeFilter";
import { UserFilter } from "./UserFilter";
import { useAuditLogs } from "../../../../Contexts/AuditLogsContext";
import { useLocalizedStrings } from "../../../../Localization";
import { FilterParams } from "../../types";

export const AuditLogFilters: React.FC = () => {
  const L = useLocalizedStrings();
  const { 
    auditLogsFilters, 
    onAuditLogsFiltersChange,
    filterOptions
  } = useAuditLogs();
  
  // Local state for managing filters before applying them
  const [localFilters, setLocalFilters] = useState<FilterParams>({
    startDate: auditLogsFilters.startDate || "",
    endDate: auditLogsFilters.endDate || "",
    events: auditLogsFilters.events || [],
    userIds: auditLogsFilters.userIds || [],
  });

  // Update local filters when context filters change
  useEffect(() => {
    setLocalFilters({
      startDate: auditLogsFilters.startDate || "",
      endDate: auditLogsFilters.endDate || "",
      events: auditLogsFilters.events || [],
      userIds: auditLogsFilters.userIds || [],
    });
  }, [auditLogsFilters]);

  // Get available options from the API response
  const { availableEvents, availableUsers } = useMemo(() => {
    return {
      availableEvents: (filterOptions?.events || []).sort(),
      availableUsers: (filterOptions?.users || []).sort((a, b) => a.userName.localeCompare(b.userName)),
    };
  }, [filterOptions]);

  // Filter handlers
  const handleStartDateChange = useCallback((date: string) => {
    setLocalFilters({ ...localFilters, startDate: date });
  }, [localFilters]);

  const handleEndDateChange = useCallback((date: string) => {
    setLocalFilters({ ...localFilters, endDate: date });
  }, [localFilters]);

  const handleEventsChange = useCallback((events: string[]) => {
    setLocalFilters({ ...localFilters, events });
  }, [localFilters]);

  const handleUsersChange = useCallback((users: string[]) => {
    setLocalFilters({ ...localFilters, userIds: users });
  }, [localFilters]);

  // Apply filters function
  const applyFilters = useCallback(() => {
    const cleanFilters = Object.fromEntries(
      Object.entries(localFilters).filter(([_, value]) => 
        value !== "" && (Array.isArray(value) ? value.length > 0 : true)
      )
    );
    onAuditLogsFiltersChange(cleanFilters);
  }, [localFilters, onAuditLogsFiltersChange]);

  // Reset filters function
  const resetFilters = useCallback(() => {
    const emptyFilters = {
      startDate: "",
      endDate: "",
      events: [],
      userIds: [],
    };
    setLocalFilters(emptyFilters);
    onAuditLogsFiltersChange({});
  }, [onAuditLogsFiltersChange]);

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 lg:grid-cols-[4fr_3fr_3fr] gap-2 w-full">
        <DateRangeFilter
          startDate={localFilters.startDate || ""}
          endDate={localFilters.endDate || ""}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
        />
        <EventTypeFilter
          selectedEvents={localFilters.events || []}
          availableEvents={availableEvents}
          onEventsChange={handleEventsChange}
        />
        <UserFilter
          selectedUsers={localFilters.userIds || []}
          availableUsers={availableUsers}
          onUsersChange={handleUsersChange}
        />
      </div>
      <div className="pt-3">
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" onClick={resetFilters}>{L.admin_panel.audit_logs.reset_filters}</Button>
          <Button onClick={applyFilters}>{L.admin_panel.audit_logs.apply_filters}</Button>
        </div>
      </div>
    </div>
  );
};